import { definedNotNull, voucherIdSchema } from '@df/common';
import { z } from 'zod';
import { accountReferenceSchema } from './account';
import { cashFlowTypeSchema } from './cash_flow';
import { bookYearSchema } from './periods';
import { knownTransactionTypeSchema } from './transaction_type';
import { voucherEntryIdSchema } from './voucher_entry_id';

import { voucherNumberSchema, voucherSeriesSchema } from './voucher_id';

const nullableOrUndefinedToNull = <Type>(type: Type | null | undefined) =>
  definedNotNull(type) ? type : null;

const explicitBookYearFilterSchema = bookYearSchema;

const relativeMonthFilterRangeSchema = z.object({
  beforeBookYearEnd: z.number(),
  afterBookYearEnd: z.number(),
});
type RelativeMonthRangeFilter = z.infer<typeof relativeMonthFilterRangeSchema>;

const relativeMonthFilterSchema = z.object({
  range: relativeMonthFilterRangeSchema,
  currentBookYear: bookYearSchema,
});
type RelativeMonthFilter = z.infer<typeof relativeMonthFilterSchema>;

const bookYearFilterSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('Explicit'), years: explicitBookYearFilterSchema.array() }),
  z.object({ type: z.literal('WithoutCurrentBookYear'), range: relativeMonthFilterRangeSchema }),
  z.object({ type: z.literal('WithCurrentBookYear') }).extend(relativeMonthFilterSchema.shape),
]);
type BookYearFilter = z.infer<typeof bookYearFilterSchema>;

const monthFilterSchema = z.object({
  months: z.number().array(),
});
type MonthFilter = z.infer<typeof monthFilterSchema>;

const monthDaySchema = z.union([
  z.object({
    type: z.literal('Day'),
    data: z.number(),
  }),
  z.object({
    type: z.literal('LastDayInMonth'),
  }),
]);
type MonthDay = z.infer<typeof monthDaySchema>;

const closenessSchema = z.union([
  z.literal('Exact'),
  z.literal('AdjacentDay'),
  z.literal('AdjacentBankDay'),
]);
type Closeness = z.infer<typeof closenessSchema>;

const monthDayFilterSchema = z.object({
  closeness: closenessSchema,
  monthDays: monthDaySchema.array(),
});
type MonthDayFilter = z.infer<typeof monthDayFilterSchema>;

const dateFilterSchema = z.object({
  min: z.string().nullable().optional().transform(nullableOrUndefinedToNull),
  max: z.string().nullable().optional().transform(nullableOrUndefinedToNull),
  monthDayFilter: monthDayFilterSchema.nullable().optional().transform(nullableOrUndefinedToNull),
});
type DateFilter = z.infer<typeof dateFilterSchema>;

const voucherFilterSchema = z
  .object({
    series: voucherSeriesSchema,
    from: voucherNumberSchema.nullable().optional().transform(nullableOrUndefinedToNull),
    to: voucherNumberSchema.nullable().optional().transform(nullableOrUndefinedToNull),
  })
  .array();
type VoucherFilter = z.infer<typeof voucherFilterSchema>;

const accountGroupFilterSchema = z.string().array();
type AccountGroupFilter = z.infer<typeof accountGroupFilterSchema>;

const accountRangeSchema = z.object({
  isBasAccountFilter: z
    .boolean()
    .optional()
    .transform((value) => (definedNotNull(value) ? value : false)),
  from: accountReferenceSchema.optional(),
  to: accountReferenceSchema.optional(),
});
const accountFilterSchema = accountRangeSchema.array();
type AccountFilter = z.infer<typeof accountFilterSchema>;

const fieldSchema = z.union([
  z.literal('EntryText'),
  z.literal('VoucherText'),
  z.literal('VoucherSeries'),
  z.literal('VoucherNumber'),
  /// Search for a voucher by name, e.g. A-10
  z.literal('VoucherName'),
]);
const matchOptionSchema = z.union([
  /// Match needle only if it is a prefix in the haystack, e.g. "jan" matches "janne" but "ann" does not.
  z.literal('ExactPrefixOnly'),
  /// Match needle if it is a substring in the haystack, e.g. "ann" matches "janne" but "ane" does not.
  z.literal('ExactSubstring'),
  /// Match needle if it is a subsequence in the haystack, e.g. "ane" matches "janne".
  z.literal('Subsequence'),
  /// Match needle if its edit distance to a prefix in the haystack is less than N=2 (Damerau-Levenshtein).
  ///
  /// See https://en.wikipedia.org/wiki/Damerau%E2%80%93Levenshtein_distance
  z.literal('PrefixDamerauLevenshtein'),
]);
const textFilterSchema = z.object({
  needle: z.string(),
  fields: fieldSchema.array().nullable().optional().transform(nullableOrUndefinedToNull),
  matchOption: matchOptionSchema.array().nullable().optional().transform(nullableOrUndefinedToNull),
});
type TextFilter = z.infer<typeof textFilterSchema>;

const amountFilterSchema = z.object({
  isAbsolute: z.boolean(),
  from: z.string().nullable().optional(),
  to: z.string().nullable().optional(),
});
type AmountFilter = z.infer<typeof amountFilterSchema>;

const transactionTypeFilterSchema = z.object({
  transactionType: knownTransactionTypeSchema.nullish(),
  isReversed: z.boolean().nullish(),
});
// Called SingleTransactionTypeFilter in the backend
type TransactionTypeFilter = z.infer<typeof transactionTypeFilterSchema>;

const cashFlowFilterSchema = z.object({
  cashFlow: cashFlowTypeSchema.optional(),
});
type CashFlowFilter = z.infer<typeof cashFlowFilterSchema>;

const dimensionAndUnitFilterSchema = z.object({
  dimension: z.string(),
  unit: z.string(),
});
type DimensionAndUnitFilter = z.infer<typeof dimensionAndUnitFilterSchema>;

const filterModeSchema = z.union([z.literal('Include'), z.literal('Exclude')]);
type FilterMode = z.infer<typeof filterModeSchema>;

const balanceFilterSchema = z.discriminatedUnion('type', [
  z.object({
    mode: filterModeSchema,
    type: z.literal('AccountGroup'),
    data: accountGroupFilterSchema,
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('Account'),
    data: accountFilterSchema,
  }),
]);

const nonBalanceFilterSchema = z.discriminatedUnion('type', [
  z.object({
    mode: filterModeSchema,
    type: z.literal('BookYear'),
    data: bookYearFilterSchema,
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('Date'),
    data: dateFilterSchema,
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('Month'),
    data: monthFilterSchema,
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('MonthDay'),
    data: monthDayFilterSchema,
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('Voucher'),
    data: voucherFilterSchema,
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('Text'),
    data: textFilterSchema,
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('Amount'),
    data: amountFilterSchema,
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('TransactionType'),
    data: transactionTypeFilterSchema.array(),
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('CashFlow'),
    data: cashFlowFilterSchema.array(),
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('AccountCombination'),
    data: z.string(),
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('DimensionAndUnit'),
    data: dimensionAndUnitFilterSchema.array(),
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('VoucherId'),
    data: voucherIdSchema.array(),
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('VoucherEntryId'),
    data: voucherEntryIdSchema.array(),
  }),
  z.object({
    mode: filterModeSchema,
    type: z.literal('QueryText'),
    data: z.null(),
  }),
]);

const mapOrObjectToObjectSchema = <Schema extends z.ZodType>(schema: Schema) =>
  z.preprocess((value) => {
    if (value instanceof Map) {
      return Object.fromEntries(value);
    }

    return value;
  }, schema);

const filterSchema = mapOrObjectToObjectSchema(
  z.discriminatedUnion('type', [...balanceFilterSchema.options, ...nonBalanceFilterSchema.options]),
);
type Filter = z.infer<typeof filterSchema>;

const balanceFilterOptionsSchema = z.array(balanceFilterSchema);

const filterOptionsSchema = filterSchema.array();
const nullableFilterOptionsSchema = filterOptionsSchema.nullable().optional();
type NullableFilterOptions = z.infer<typeof nullableFilterOptionsSchema>;

export {
  accountRangeSchema,
  balanceFilterOptionsSchema,
  dimensionAndUnitFilterSchema,
  filterOptionsSchema,
  nullableFilterOptionsSchema,
  voucherFilterSchema,
  type AccountFilter,
  type AccountGroupFilter,
  type AmountFilter,
  type BookYearFilter,
  type CashFlowFilter,
  type Closeness,
  type DateFilter,
  type DimensionAndUnitFilter,
  type Filter,
  type FilterMode,
  type MonthDay,
  type MonthDayFilter,
  type MonthFilter,
  type NullableFilterOptions,
  type RelativeMonthFilter,
  type RelativeMonthRangeFilter,
  type TextFilter,
  type TransactionTypeFilter,
  type VoucherFilter,
};
